import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Sparkles, MessageCircle, Settings, X } from 'lucide-react';
import { Card, Button, Input } from '../ui';
import { cn } from '../../lib/utils';

// Hardcoded arrays for the slot machine rings
const RING_1 = [
  "A hilarious", "An awkward", "A terrifying", "The absolute worst", "The most surprising", 
  "A heartwarming", "An embarrassing", "The best", "A chaotic", "A bizarre",
  "A life-changing", "A disgusting", "An expensive", "A secret", "A misunderstood",
  "A ridiculous", "An exhausting", "A magical", "A confusing", "A spontaneous",
  "A painful", "An unforgettable", "A messy", "A rebellious", "A proud"
];

const RING_2 = [
  "childhood memory", "first date", "family vacation", "cooking disaster", "job interview",
  "animal encounter", "roommate situation", "holiday dinner", "purchase", "fashion choice",
  "DIY project", "school presentation", "text message", "road trip", "gift exchange",
  "sports moment", "concert experience", "celebrity encounter", "home repair", "workout",
  "wedding memory", "airport experience", "camping trip", "restaurant visit", "party"
];

const RING_3 = [
  "that you deeply regret.", "that nobody believes.", "involving the police.", 
  "that went totally viral (or should have).", "that cost way too much money.",
  "involving a complete stranger.", "that ruined your favorite clothes.", 
  "that you hid from your parents.", "that ended up in the ER.", 
  "that you still think about at 3 AM.", "involving severe weather.",
  "that started with a simple lie.", "that you caught on camera.",
  "involving an ex.", "that won an argument.", "that you'll tell your grandkids.",
  "that broke something valuable.", "that resulted in a nickname.",
  "involving a terrible haircut.", "that got you promoted (or fired)."
];

export function IceBreakerSlotMachine() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [results, setResults] = useState<[number, number, number]>([0, 0, 0]);
  
  const [showSettings, setShowSettings] = useState(false);
  const [customR1, setCustomR1] = useState(RING_1.join('\n'));
  const [customR2, setCustomR2] = useState(RING_2.join('\n'));
  const [customR3, setCustomR3] = useState(RING_3.join('\n'));

  // To create the infinite spin effect, we need a massive array of items for each column.
  // We'll generate a visual track of 50 items per column, with the target answer at the very end.
  const [tracks, setTracks] = useState<{ col1: string[], col2: string[], col3: string[] }>({
    col1: RING_1.slice(0, 5),
    col2: RING_2.slice(0, 5),
    col3: RING_3.slice(0, 5)
  });

  const getActiveArray = (customText: string, fallback: string[]) => {
    const parsed = customText.split('\n').map(s => s.trim()).filter(s => s.length > 0);
    return parsed.length > 0 ? parsed : fallback;
  };

  const generateTrack = (sourceArray: string[], targetIndex: number) => {
    const track: string[] = [];
    // Add 40 random items
    for(let i=0; i<40; i++) {
      track.push(sourceArray[Math.floor(Math.random() * sourceArray.length)]);
    }
    // Add the target item at the end (index 40)
    track.push(sourceArray[targetIndex]);
    return track;
  };

  const spin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setShowSettings(false); // Hide settings when spinning

    const arr1 = getActiveArray(customR1, RING_1);
    const arr2 = getActiveArray(customR2, RING_2);
    const arr3 = getActiveArray(customR3, RING_3);

    const r1 = Math.floor(Math.random() * arr1.length);
    const r2 = Math.floor(Math.random() * arr2.length);
    const r3 = Math.floor(Math.random() * arr3.length);

    setResults([r1, r2, r3]);

    setTracks({
      col1: generateTrack(arr1, r1),
      col2: generateTrack(arr2, r2),
      col3: generateTrack(arr3, r3)
    });

    // Reset spinning state after animations complete
    // Col 3 takes the longest (4 seconds)
    setTimeout(() => {
      setIsSpinning(false);
    }, 4500);
  };

  // Pre-fill initial view
  useEffect(() => {
    const arr1 = getActiveArray(customR1, RING_1);
    const arr2 = getActiveArray(customR2, RING_2);
    const arr3 = getActiveArray(customR3, RING_3);
    
    queueMicrotask(() => {
      setTracks({
        col1: generateTrack(arr1, 0),
        col2: generateTrack(arr2, 0),
        col3: generateTrack(arr3, 0)
      });
    });
  }, [customR1, customR2, customR3]);

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-4">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-800 rounded-3xl p-8 md:p-12 text-white shadow-2xl flex flex-col items-center text-center relative overflow-hidden">
        <Sparkles className="absolute top-4 right-8 w-16 h-16 text-yellow-300 opacity-30 animate-pulse" />
        <MessageCircle className="absolute bottom-4 left-8 w-24 h-24 text-white opacity-10 -rotate-12" />
        
        <h2 className="text-4xl md:text-6xl font-black mb-4 tracking-tight drop-shadow-md z-10">
          The Ice Breaker <span className="text-yellow-400">Slot Machine</span>
        </h2>
        <p className="text-indigo-100 max-w-2xl text-lg md:text-xl font-medium z-10 mb-8">
          Hook up your laptop to the projector. Spin the reels to generate a completely randomized, hilarious story prompt for your team or audience!
        </p>

        <div className="flex flex-col sm:flex-row gap-4 z-10">
          <Button 
            onClick={spin}
            disabled={isSpinning}
            className="h-20 px-12 text-3xl font-black bg-yellow-400 hover:bg-yellow-500 text-indigo-900 shadow-[0_0_40px_rgba(250,204,21,0.5)] rounded-full border-4 border-yellow-200"
          >
            {isSpinning ? 'SPINNING...' : <><Play className="w-8 h-8 mr-3 fill-indigo-900" /> PULL LEVER</>}
          </Button>
          
          <Button 
            onClick={() => setShowSettings(!showSettings)}
            disabled={isSpinning}
            variant="secondary"
            className="h-20 px-8 text-xl font-bold border-4 border-indigo-400 text-white hover:bg-indigo-700/50 rounded-full bg-indigo-900/40 backdrop-blur-sm"
          >
            <Settings className="w-6 h-6 mr-2" /> Custom Inputs
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <Card className="p-6 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 shadow-xl relative">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowSettings(false)}
                className="absolute top-4 right-4 text-slate-500"
              >
                <X className="w-5 h-5" />
              </Button>
              <h3 className="text-xl font-bold mb-2 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Custom Slot Machine Reels
              </h3>
              <p className="text-sm text-slate-500 mb-6">Replace the default arrays with your own options! Enter one item per line.</p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-blue-500 mb-2">Reel 1 (Blue)</label>
                  <textarea
                    value={customR1}
                    onChange={(e) => setCustomR1(e.target.value)}
                    className="w-full h-64 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none resize-none text-sm"
                  />
                  <div className="text-xs text-slate-400 mt-1">{getActiveArray(customR1, RING_1).length} items</div>
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-purple-500 mb-2">Reel 2 (Purple)</label>
                  <textarea
                    value={customR2}
                    onChange={(e) => setCustomR2(e.target.value)}
                    className="w-full h-64 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-purple-500 outline-none resize-none text-sm"
                  />
                  <div className="text-xs text-slate-400 mt-1">{getActiveArray(customR2, RING_2).length} items</div>
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wider text-rose-500 mb-2">Reel 3 (Red)</label>
                  <textarea
                    value={customR3}
                    onChange={(e) => setCustomR3(e.target.value)}
                    className="w-full h-64 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-rose-500 outline-none resize-none text-sm"
                  />
                  <div className="text-xs text-slate-400 mt-1">{getActiveArray(customR3, RING_3).length} items</div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The Slot Machine */}
      <div className="bg-slate-900 p-4 md:p-8 rounded-[3rem] shadow-2xl border-[12px] border-slate-800">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          
          {/* Column 1: Descriptor */}
          <SlotColumn 
            items={tracks.col1} 
            isSpinning={isSpinning} 
            duration={2} 
            delay={0}
            color="bg-blue-500"
          />

          {/* Column 2: Subject */}
          <SlotColumn 
            items={tracks.col2} 
            isSpinning={isSpinning} 
            duration={3} 
            delay={0.2}
            color="bg-purple-500"
          />

          {/* Column 3: Twist */}
          <SlotColumn 
            items={tracks.col3} 
            isSpinning={isSpinning} 
            duration={4} 
            delay={0.4}
            color="bg-rose-500"
          />

        </div>
      </div>
    </div>
  );
}

function SlotColumn({ 
  items, 
  isSpinning, 
  duration, 
  delay,
  color 
}: { 
  items: string[], 
  isSpinning: boolean, 
  duration: number,
  delay: number,
  color: string
}) {
  // Height of each slot item card
  const ITEM_HEIGHT = 160; 
  const GAP = 16;
  const TOTAL_HEIGHT = ITEM_HEIGHT + GAP;

  return (
    <div className="h-[200px] md:h-[240px] bg-slate-800 rounded-2xl border-4 border-slate-700 shadow-inner overflow-hidden relative flex items-center justify-center">
      
      {/* Highlight overlay */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[176px] bg-white/5 border-y-4 border-white/10 z-20 pointer-events-none"></div>

      <div className="w-full px-4 relative z-10">
        <div 
          className="flex flex-col gap-4 w-full absolute top-1/2"
          style={{
            // Translate up so that the last item (index 40) lands perfectly in the center
            // Initial state (setup) we just sit at translateY 0
            transform: isSpinning 
              ? `translateY(calc(-50% - ${40 * TOTAL_HEIGHT}px))` 
              : `translateY(calc(-50% - ${40 * TOTAL_HEIGHT}px))`, // Keep it landed on the target after spin
            transition: isSpinning 
              ? `transform ${duration}s cubic-bezier(0.15, 0.85, 0.2, 1) ${delay}s` 
              : 'none',
            // Initial flash reset trick: when NOT spinning, if we just mounted, we want it at the end.
            // But right before spinning, we quickly reset to 0 (no transition) then apply the transform with transition.
            // React handles this re-render, but for a pure CSS slot machine, a key change is best.
          }}
          key={items.join('')} // Force re-render of the DOM node when tracks update to reset position instantly
        >
          {items.map((text, idx) => (
            <div 
              key={idx} 
              className={cn(
                "h-[160px] w-full rounded-xl shadow-lg border-2 border-white/20 flex items-center justify-center p-6 text-center shrink-0",
                color
              )}
            >
              <span className="text-2xl md:text-3xl font-black text-white leading-tight drop-shadow-md">
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Glossy shine effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/30 pointer-events-none z-30"></div>
    </div>
  );
}
